import { Checkbox, Flex } from "@chakra-ui/react";
import { useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { ColumnDefinition, RanaTable, TABLE_SELECTION_COLUMN_WIDTH } from "@nens/ranalib";
import { displayBytes } from "@nens/shared-utils";

import { Profile } from "@/lib/profile";
import { FileFeatureTypeEnum } from "@/lib/rana-file-data-type";
import { getPrettyDate } from "@/utils/get-pretty-time";
import { isTypedDescriptor } from "@/utils/is-descriptor-type";

import { getFiles } from "../../store/selectors";
import { usePublicationStore } from "../../store/store";
import { CompleteFile } from "../../types/file-reference-with-file";
import { getFileLayers, getFileName } from "../../utils/files";
import { PublicationDetailFileAdd } from "../detail/components/file-add";
import { SelectedFileOrLayer } from "../map-layers";

import { useFileLayerSelection } from "./hooks/use-file-layer-selection";

type AllLayerRowProps = {
  layer: "all";
  isSelected: boolean;
  onCheckboxChange: (checked: boolean) => void;
};

type OneLayerRowProps = {
  layer: SelectedFileOrLayer;
  isSelected: boolean;
  onCheckboxChange: (
    checked: boolean,
    file: CompleteFile,
    name: string,
    layerInFile: string | undefined
  ) => void;
};

type LayerRowProps = OneLayerRowProps | AllLayerRowProps;

const LayerRow = ({ layer, isSelected, onCheckboxChange }: LayerRowProps) => {
  const intl = useIntl();
  const isAll = layer === "all";
  const title = isAll
    ? intl.formatMessage({
        id: "publication.map.layermodal.all_layers",
        defaultMessage: "All Layers",
      })
    : layer.name;
  return (
    <RanaTable.Row.Accordion.Panel
      title={title}
      {...(isAll
        ? { rowId: "all", onClick: () => onCheckboxChange(!isSelected) }
        : {
            rowId: layer.name,
            onClick: () => onCheckboxChange(!isSelected, layer.file, layer.name, layer.layerInFile),
          })}
    >
      <Flex w={TABLE_SELECTION_COLUMN_WIDTH}>
        <Checkbox pointerEvents="none" flexShrink={0} isChecked={isSelected} />
      </Flex>

      <Flex fontStyle={isAll ? "italic" : undefined} isTruncated>
        {title}
      </Flex>
    </RanaTable.Row.Accordion.Panel>
  );
};

interface FileRowProps {
  file: CompleteFile;
  onLayerCheckboxChange: (
    checked: boolean,
    file: CompleteFile,
    name: string,
    layerInFile: string | undefined
  ) => void;
  isLayerSelected: (fileId: string, layerInFile: string | undefined) => boolean;
  handleAllLayerCheckbox: (checked: boolean, file: CompleteFile) => void;
}

const FileRow = ({
  file,
  onLayerCheckboxChange,
  isLayerSelected,
  handleAllLayerCheckbox,
}: FileRowProps) => {
  const fileData = file.file;
  const fileName = getFileName(file);
  const fileLayers = getFileLayers(file);
  const fileModifiedDate = getPrettyDate(fileData.lastModified);
  const defaultIsOpen = fileLayers.length === 1; // Auto-expand single-layer files

  if (!fileLayers.length) {
    return null;
  }

  return (
    <RanaTable.Row.Accordion defaultIsOpen={defaultIsOpen} key={file.path}>
      <RanaTable.Row.Accordion.Button rowId={file.path}>
        <RanaTable.Cell>{fileName}</RanaTable.Cell>
        <RanaTable.Cell>{displayBytes(fileData.size)}</RanaTable.Cell>
        <RanaTable.Cell title={file.ref}>{file.ref?.slice(0, 7)}</RanaTable.Cell>
        <RanaTable.Cell>
          <Profile inTable user={fileData.user} />
        </RanaTable.Cell>
        <RanaTable.Cell>{fileModifiedDate}</RanaTable.Cell>
      </RanaTable.Row.Accordion.Button>
      {fileLayers.length > 1 && (
        <LayerRow
          layer="all"
          isSelected={fileLayers.every(({ layerInFile }) =>
            isLayerSelected(file.path, layerInFile)
          )}
          onCheckboxChange={(checked) => {
            handleAllLayerCheckbox(checked, file);
          }}
        />
      )}
      {fileLayers.map((layer) => (
        <LayerRow
          key={layer.file.path + layer.layerInFile}
          layer={layer}
          isSelected={isLayerSelected(file.path, layer.layerInFile)}
          onCheckboxChange={onLayerCheckboxChange}
        />
      ))}
    </RanaTable.Row.Accordion>
  );
};

interface PublicationFilesTableProps {
  selectedItems: SelectedFileOrLayer[];
  onSelectionChange: (items: SelectedFileOrLayer[]) => void;
  mode?: "layers" | "study-area";
}

const isAbleToBeVisualized = (file: CompleteFile) => {
  const { dataType } = file.file.descriptor;
  return dataType === "raster" || dataType === "vector" || dataType === "scenario";
};

const isStudyAreaFile = (file: CompleteFile) => {
  const { descriptor } = file.file;
  return (
    isTypedDescriptor(descriptor, "vector") &&
    descriptor.meta?.featureTypeDefinition === FileFeatureTypeEnum.StudyArea
  );
};

export const PublicationFilesTable = ({
  selectedItems,
  onSelectionChange,
  mode = "layers",
}: PublicationFilesTableProps) => {
  const intl = useIntl();
  const publicationFiles = usePublicationStore(getFiles);
  const [searchString, setSearchString] = useState("");

  const filteredPublicationFiles = useMemo(() => {
    return publicationFiles.filter(
      (file) =>
        // Is able to be visualized in a publication
        isAbleToBeVisualized(file) &&
        // Is a study area file if in study-area mode, or is not a study area file if in layers mode
        (mode === "study-area" ? isStudyAreaFile(file) : !isStudyAreaFile(file)) &&
        // Matches the search string
        getFileName(file).toLowerCase().includes(searchString.toLowerCase())
    );
  }, [mode, publicationFiles, searchString]);

  const { handleLayerCheckbox, handleAllLayerCheckbox, isLayerSelected } = useFileLayerSelection({
    selectedItems,
    onSelectionChange,
  });

  const columnDefinitions: ColumnDefinition[] = [
    {
      // checkboxes
      title: "",
      width: TABLE_SELECTION_COLUMN_WIDTH,
    },
    {
      title: intl.formatMessage({
        id: "publication.files.column.name",
        defaultMessage: "Name",
      }),
      width: "table_cell_xs",
    },
    {
      title: intl.formatMessage({
        id: "publication.files.column.size",
        defaultMessage: "Size",
      }),
      width: "table_cell_xs",
    },
    {
      title: intl.formatMessage({
        id: "publication.files.column.version",
        defaultMessage: "Version",
      }),
      width: "table_cell_xs",
    },
    {
      title: intl.formatMessage({
        id: "publication.files.column.added_by",
        defaultMessage: "Added by",
      }),
      width: "table_cell_xs",
    },
    {
      title: intl.formatMessage({
        id: "publication.files.column.last_modified",
        defaultMessage: "Last modified",
      }),
      width: "table_cell_xs",
    },
  ];

  return (
    <RanaTable
      columnDefinitions={columnDefinitions}
      tableDataIds={filteredPublicationFiles.map((file) => file.path)}
      emptyElement={
        <RanaTable.Empty>
          {mode === "study-area" ? (
            <FormattedMessage
              id="publication.map.studyareamodal.no_files"
              defaultMessage="There are no study area files in the publication yet."
            />
          ) : (
            <FormattedMessage
              id="publication.map.layermodal.no_files"
              defaultMessage="There are no files in the publication that can be shown on the map."
            />
          )}
        </RanaTable.Empty>
      }
    >
      <Flex>
        <RanaTable.Filters
          search={{
            placeholder: intl.formatMessage({
              id: "publication.map.layermodal.search",
              defaultMessage: "Find your file",
            }),
            searchString,
            setSearchString,
          }}
        />
        <PublicationDetailFileAdd />
      </Flex>

      <RanaTable.Container sx={{ minHeight: "unset" }}>
        <RanaTable.Header />
        <RanaTable.Body>
          {filteredPublicationFiles.map((file) => (
            <FileRow
              key={file.path}
              file={file}
              onLayerCheckboxChange={handleLayerCheckbox}
              isLayerSelected={isLayerSelected}
              handleAllLayerCheckbox={handleAllLayerCheckbox}
            />
          ))}
        </RanaTable.Body>
      </RanaTable.Container>
    </RanaTable>
  );
};
